import _ from 'lodash'
import {MutationTree} from 'vuex'
import {MediaState} from '@/modules/media/store'
import {EntityState, MediaObject, MediaUpload, MediaUploadState, QueueState} from '@/modules/media/types'

export enum MutationTypes {
    SetMediaObjects = 'objects/set',
    AddMediaObject = 'object/add',
    SetCurrentMedia = 'objects/current/set',
    UpdateMediaField = 'objects/field/update',
    DeleteMediaObject = 'object/delete',
    MarkMediaObjectUpdated = 'object/mark/updated',
    UpdateMediaObject = 'object/update',
    UpdateCurrentMediaObjectField = 'object/current/field/update',
    SetObjectSelection = 'SET_SELECTED_OBJECTS',
    SetDeletingSelection = 'object/selection/deleting/set',
    ShowDeleteSelectionConfirm = 'selection/delete/confirm/show',
    // Mutaciones para el administrador de subidas
    ClearUploadQueue = 'uploadQueue/clear',

    PushToUploadQueue = 'uploadQueue/push',
    UpdateMediaUpload = 'mediaUpload/update',
    UpdateMediaUploadField = 'mediaUpload/field/update',
    SetMediaUploadState = 'mediaUpload/state/set',
    SetMediaUploadProgress = 'mediaUpload/progress/set',
    DeleteMediaUpload = 'DELETE_MEDIA_UPLOAD',
    SetMediaUploadThumbnail = 'mediaUpload/thumbnail/set',
    SetCurrentUpload = 'currentUpload/set',
    ShowUploadManager = 'uploadManager/show',
    SetQueueState = 'SET_QUEUE_STATE',
    SetUploadMaxSize = 'SET_UPLOAD_MAX_SIZE',
    SetAllowedTypes = 'SET_ALLOWED_TYPES',
    SetThumbnailSizes = 'SET_THUMBNAIL_SIZES',
    ToggleUploadConstrainsFetched = 'TOGGLE_UPLOAD_CONSTRAINS_FETCHED',
}

/**
 * Busca un objeto multimedia registrado en el estado según si id
 *
 * @param state
 * @param id
 */
const findMediaObjectIndex = (state: MediaState, id: number) => state.objects.findIndex(mo => mo.id === id)

/**
 * Actualiza un campo de un objeto de subida
 *
 * @param state Estado de la aplicación
 * @param id Id del objeto de subida a actualizar
 * @param field Campo a actualizar
 * @param value Nuevo valor
 */
const updateMediaUpload = (state: MediaState, id: string, field: string, value: any) => {
    const index = state.uploadQueue.findIndex(mo => mo.id === id)
    if (index < 0) {
        throw new Error(`No existe el objeto de carga con id ${id}`)
    }

    const media = state.uploadQueue[index]
    _.set(media, field, value)
    state.uploadQueue.splice(index, 1, media)

    return media
}

/**
 * Actualiza un campo de un objeto multimedia registrado en la aplicación
 *
 * @param  {MediaState} state
 * @param  {number} id
 * @param  {string} field
 * @param  {any} value
 */
const updateMedia = (state: MediaState, id: number, field: string, value: any) => {
    const index = findMediaObjectIndex(state, id)
    if (index < 0) {
        throw new Error(`No existe el objeto multimedia con id ${id}`)
    }

    const media = state.objects[index]
    _.set(media, field, value)
    media.state = EntityState.Modified

    state.objects.splice(index, 1, media)

    return media
}

/**
 * Cambia el estado de un objeto multimedia para indica si ha sido modificado
 *
 * @param state
 * @param id
 * @param entityState
 */
const setMediaObjectState = (state: MediaState, id: number, entityState: EntityState) => {
    const index = findMediaObjectIndex(state, id)
    if (index < 0) {
        return
    }

    const media = state.objects[index]
    media.state = EntityState.Updated
    state.objects.splice(index, 1, media)

    if (state.currentMedia.id === id) {
        state.currentMedia.state = EntityState.Updated
    }
}

const mutations: MutationTree<MediaState> = {

    /**
     * Registra en el estado una lista de objetos multimedia
     */
    [MutationTypes.SetMediaObjects]: (state, objects: MediaObject[]) => state.objects = objects,

    /**
     * Limpia la cola de subida y cualquier subida actualmente en edición
     */
    [MutationTypes.ClearUploadQueue]: (state) => {
        state.uploadQueue = []
        state.queueState = QueueState.Open
        state.currentUpload = ''
        state.hasSelectedUpload = false
    },

    /**
     * Añade un nuevo archivo al final de la cola de subida
     */
    [MutationTypes.PushToUploadQueue]: (state, media: MediaUpload) => state.uploadQueue.push(media),

    /**
     * Añade un nuevo objeto multimedia a la lista registrada en el estado
     *
     * Si el objeto ya existe se reemplazará por el nuevo
     */
    [MutationTypes.AddMediaObject]: (state, media: MediaObject) => {
        const index = state.objects.findIndex(m => m.id === media.id)
        index === -1
            ? state.objects.push(media)
            : state.objects.splice(index, 1, media)
    },

    /**
     * Actualiza un objeto multimedia almacenado en la cola de archivos de subida
     */
    [MutationTypes.UpdateMediaUpload]: (state, upload: MediaUpload) => {
        const index = state.uploadQueue.findIndex(mp => mp.id === upload.id)
        if (index < 0) {
            throw new Error(`No existe el objeto de carga con id ${upload.id}`)
        }

        state.uploadQueue.splice(index, 1, upload)
    },

    [MutationTypes.UpdateMediaUploadField]: (state, payload: { id: string, field: string, value: any }) => {
        updateMediaUpload(state, payload.id, payload.field, payload.value)
    },

    /**
     * Actualiza el estado de un archivo de subida
     */
    [MutationTypes.SetMediaUploadState]: (state, payload: { id: string, state: MediaUploadState }) => {
        updateMediaUpload(state, payload.id, 'state', payload.state)
    },

    /**
     * Actualiza el progreso de subida de un archivo
     */
    [MutationTypes.SetMediaUploadProgress]: (state, payload: { id: string, progress: number }) => {
        updateMediaUpload(state, payload.id, 'progress', payload.progress)
    },

    // Define la miniatura de un archivo de subida
    [MutationTypes.SetMediaUploadThumbnail]: (state, payload: { id: string, dataURL: string }) => {
        updateMediaUpload(state, payload.id, 'thumbnail', payload.dataURL)
    },

    /**
     * Define el id del archivo de subida actualmente en edición
     */
    [MutationTypes.SetCurrentUpload]: (state, upload: string) => {
        state.currentUpload = upload
        state.hasSelectedUpload = true
    },

    [MutationTypes.ShowUploadManager]: (state, show: boolean) => {
        state.showUploadManager = show
    },

    [MutationTypes.SetCurrentMedia]: (state, current: MediaObject) => {
        state.currentMedia = _.clone(current)
        state.hasSelectedMedia = true
    },

    /**
     * Actualiza una propiedad del objeto multimedia con id argumentado
     *
     * @param  {MediaState} state
     * @param  {id:number} payload
     * @param  {string} field Nombre de la propiedad a actualizar
     * @param  {any} value Nuevo valor
     */
    [MutationTypes.UpdateMediaField]: (state, payload: { id: number, field: string, value: any }) => {
        updateMedia(state, payload.id, payload.field, payload.value)
    },

    /**
     * Elimina un objeto multimedia del estado
     *
     * @param { number } id Id del objeto a eliminar
     */
    [MutationTypes.DeleteMediaObject]: (state, id: number): boolean => {
        const index = state.objects.findIndex(mo => mo.id === id)
        if (index < 0) {
            return false
        }

        state.objects.splice(index, 1)

        return true
    },

    [MutationTypes.MarkMediaObjectUpdated]: (state, id: number) => {
        setMediaObjectState(state, id, EntityState.Updated)
    },

    /**
     * Actualiza un objeto multimedia en el estado
     *
     * Reemplaza completamente la entidad registrada en el estada por la entidad
     * argumentada.
     */
    [MutationTypes.UpdateMediaObject]: (state, media: MediaObject) => {
        const index = state.objects.findIndex(mo => mo.id === media.id)
        if (index < 0) {
            throw new Error(`No existe el objeto multimedia con id ${media.id}`)
        }

        state.objects.splice(index, 1, media)

        if (state.currentMedia.id === media.id) {
            state.currentMedia = _.clone(media)
        }

        // Se indica que la entidad se encuentra actualizada
        setMediaObjectState(state, media.id, EntityState.Updated)
    },

    /**
     * Actualiza un campo de la subida actualmente en edición
     */
    [MutationTypes.UpdateCurrentMediaObjectField]: (state, payload: { field: string, value: any }) => {
        const media = state.currentMedia
        _.set(media, payload.field, payload.value)

        state.currentMedia = media
    },

    /**
     * Elimina una subida de la cola
     */
    [MutationTypes.DeleteMediaUpload]: (state, id: string): boolean => {
        const index = state.uploadQueue.findIndex(mu => mu.id === id)
        if (index < 0) {
            return true
        }

        state.uploadQueue.splice(index, 1)

        return true
    },

    [MutationTypes.SetObjectSelection]: (state, objects: number[]) => state.selectedObjects = objects,
    [MutationTypes.SetDeletingSelection]: (state, deleting: boolean) => state.deletingSelection = deleting,
    [MutationTypes.ShowDeleteSelectionConfirm]: (state, show: boolean) => state.showDeleteConfirmModal = show,
    /**
     * Establece el estado actual de la cola de subida de ficheros
     *
     * @param state
     * @param queueState
     */
    [MutationTypes.SetQueueState]: (state, queueState: QueueState) => state.queueState = queueState,
    [MutationTypes.SetUploadMaxSize]: (state, size: number) => state.maxFileSize = size,
    [MutationTypes.SetAllowedTypes]: (state, allowedTypes: string[]) => state.validFileTypes = allowedTypes,
    [MutationTypes.ToggleUploadConstrainsFetched]: (state, fetched: boolean) => {
        state.uploadConstrainsFetched = fetched
    },
    [MutationTypes.SetThumbnailSizes]: (state, sizes: {[key: string]: number}) => {
        state.thumbnailSizes = sizes
    },
}

export default mutations
